import React, { useState, useEffect, useCallback } from 'react';
import api from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Plus,
  Search,
  Brain,
  Trash2,
  Edit,
  Loader2,
  Tag,
  ArrowRight,
  Link2,
  FileText,
  BookOpen,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type KnowledgeType =
  | 'entity'
  | 'term'
  | 'metric'
  | 'rule'
  | 'workflow'
  | 'memory'
  | 'preference'
  | 'data_source';

interface KnowledgeItem {
  id: string;
  entity_type: string;
  name: string;
  description: string;
  tags: string[];
  properties: Record<string, unknown>;
  category?: string;
  source?: string;
  created_at: string;
  updated_at: string;
}

interface Relationship {
  id: string;
  from_entity_id: string;
  to_entity_id: string;
  from_entity?: KnowledgeItem;
  to_entity?: KnowledgeItem;
  relationship_type: string;
  description: string;
  created_at: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const KNOWLEDGE_TYPES: { value: KnowledgeType; label: string }[] = [
  { value: 'entity', label: 'Entity' },
  { value: 'term', label: 'Term' },
  { value: 'metric', label: 'Metric' },
  { value: 'rule', label: 'Rule' },
  { value: 'workflow', label: 'Workflow' },
  { value: 'memory', label: 'Memory' },
  { value: 'preference', label: 'Preference' },
  { value: 'data_source', label: 'Data Source' },
];

const TYPE_BADGE_COLORS: Record<string, string> = {
  entity: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  business_entity: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  term: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  metric: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  rule: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  workflow: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300',
  memory: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  preference: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
  data_source: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
};

const PROPERTIES_PLACEHOLDER: Record<string, string> = {
  metric: JSON.stringify({ formula: '', unit: '', aggregation: 'sum' }, null, 2),
  data_source: JSON.stringify({ connection_type: '', schema: '', table: '' }, null, 2),
  workflow: JSON.stringify({ steps: [], triggers: [] }, null, 2),
  entity: JSON.stringify({ attributes: {} }, null, 2),
  business_entity: JSON.stringify({ attributes: {} }, null, 2),
  term: JSON.stringify({ synonyms: [], related_terms: [] }, null, 2),
  rule: JSON.stringify({ condition: '', action: '' }, null, 2),
  memory: JSON.stringify({}, null, 2),
  preference: JSON.stringify({}, null, 2),
};

const PAGE_SIZE = 20;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTypeBadgeClass(type: string): string {
  return TYPE_BADGE_COLORS[type] ?? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
}

function formatTypeLabel(type: string): string {
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function truncate(text: string, max: number): string {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '...' : text;
}

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function KnowledgePage() {
  const { toast } = useToast();

  // Tab state
  const [activeTab, setActiveTab] = useState('all');

  // Entity list state
  const [entities, setEntities] = useState<KnowledgeItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [offset, setOffset] = useState(0);

  // Memory state
  const [memories, setMemories] = useState<KnowledgeItem[]>([]);
  const [memoriesLoading, setMemoriesLoading] = useState(false);
  const [memoriesAvailable, setMemoriesAvailable] = useState(true);

  // Create / Edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<KnowledgeItem | null>(null);
  const [formSaving, setFormSaving] = useState(false);
  const [formData, setFormData] = useState({
    entity_type: 'entity' as string,
    name: '',
    description: '',
    category: '',
    tags: '',
    properties: '',
  });

  // Detail panel
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailItem, setDetailItem] = useState<KnowledgeItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [relationshipsLoading, setRelationshipsLoading] = useState(false);

  // Add relationship dialog
  const [relDialogOpen, setRelDialogOpen] = useState(false);
  const [relSaving, setRelSaving] = useState(false);
  const [relForm, setRelForm] = useState({
    targetSearch: '',
    target_entity_id: '',
    relationship_type: '',
    description: '',
  });
  const [relSearchResults, setRelSearchResults] = useState<KnowledgeItem[]>([]);
  const [relSearchLoading, setRelSearchLoading] = useState(false);

  // Delete confirm dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<KnowledgeItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ---------------------------------------------------------------------------
  // Fetch entities
  // ---------------------------------------------------------------------------

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset,
      };
      if (typeFilter && typeFilter !== 'all') {
        params.entity_type = typeFilter;
      }
      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }
      const res = await api.get('/semantic-layer/entities', { params });
      const data = res.data;
      const items = Array.isArray(data) ? data : data.items ?? data.entities ?? [];
      setEntities(items);
      setTotalCount(
        typeof data.total === 'number'
          ? data.total
          : Array.isArray(data)
          ? data.length
          : 0,
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load entities';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [offset, typeFilter, searchQuery, toast]);

  // ---------------------------------------------------------------------------
  // Fetch memories
  // ---------------------------------------------------------------------------

  const fetchMemories = useCallback(async () => {
    setMemoriesLoading(true);
    try {
      const params: Record<string, string | number> = {};
      if (searchQuery.trim()) {
        params.q = searchQuery.trim();
      }
      const res = await api.get('/knowledge/memories', { params });
      const data = res.data;
      const items: KnowledgeItem[] = (Array.isArray(data) ? data : data.items ?? data.memories ?? []).map(
        (m: Record<string, unknown>) => ({
          id: m.id as string,
          entity_type: (m.type as string) ?? 'memory',
          name: (m.name as string) ?? (m.key as string) ?? 'Memory',
          description: (m.content as string) ?? (m.description as string) ?? '',
          tags: (m.tags as string[]) ?? [],
          properties: (m.properties as Record<string, unknown>) ?? (m.metadata as Record<string, unknown>) ?? {},
          category: (m.category as string) ?? '',
          source: (m.source as string) ?? 'agent',
          created_at: (m.created_at as string) ?? '',
          updated_at: (m.updated_at as string) ?? '',
        }),
      );
      setMemories(items);
      setMemoriesAvailable(true);
    } catch {
      setMemories([]);
      setMemoriesAvailable(false);
    } finally {
      setMemoriesLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchEntities();
  }, [fetchEntities]);

  useEffect(() => {
    if (activeTab === 'all' || activeTab === 'memories') {
      fetchMemories();
    }
  }, [activeTab, fetchMemories]);

  // Reset offset when filters change
  useEffect(() => {
    setOffset(0);
  }, [searchQuery, typeFilter, activeTab]);

  // ---------------------------------------------------------------------------
  // Compute displayed items based on active tab
  // ---------------------------------------------------------------------------

  const getDisplayedItems = (): KnowledgeItem[] => {
    switch (activeTab) {
      case 'entities':
        return entities;
      case 'memories':
        return memories;
      case 'documents':
        return [];
      case 'all':
      default:
        return [...entities, ...memories];
    }
  };

  const isLoading = activeTab === 'memories' ? memoriesLoading : activeTab === 'all' ? loading || memoriesLoading : loading;
  const displayedItems = getDisplayedItems();

  // ---------------------------------------------------------------------------
  // CRUD handlers
  // ---------------------------------------------------------------------------

  const openCreate = () => {
    setEditingItem(null);
    setFormData({
      entity_type: 'entity',
      name: '',
      description: '',
      category: '',
      tags: '',
      properties: PROPERTIES_PLACEHOLDER['entity'],
    });
    setFormOpen(true);
  };

  const openEdit = (item: KnowledgeItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingItem(item);
    setFormData({
      entity_type: item.entity_type,
      name: item.name,
      description: item.description,
      category: item.category ?? '',
      tags: (item.tags ?? []).join(', '),
      properties: JSON.stringify(item.properties ?? {}, null, 2),
    });
    setFormOpen(true);
  };

  const handleFormSave = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Validation', description: 'Name is required.', variant: 'destructive' });
      return;
    }
    if (!formData.description.trim()) {
      toast({ title: 'Validation', description: 'Content/Description is required.', variant: 'destructive' });
      return;
    }

    let parsedProps: Record<string, unknown> = {};
    if (formData.properties.trim()) {
      try {
        parsedProps = JSON.parse(formData.properties);
      } catch {
        toast({ title: 'Validation', description: 'Properties must be valid JSON.', variant: 'destructive' });
        return;
      }
    }

    const payload = {
      entity_type: formData.entity_type,
      name: formData.name.trim(),
      description: formData.description.trim(),
      tags: formData.tags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
      properties: {
        ...parsedProps,
        ...(formData.category.trim() ? { category: formData.category.trim() } : {}),
      },
    };

    setFormSaving(true);
    try {
      if (editingItem) {
        await api.put('/semantic-layer/entities/' + editingItem.id, payload);
        toast({ title: 'Updated', description: `"${payload.name}" has been updated.` });
      } else {
        await api.post('/semantic-layer/entities', payload);
        toast({ title: 'Created', description: `"${payload.name}" has been created.` });
      }
      setFormOpen(false);
      fetchEntities();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Save failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setFormSaving(false);
    }
  };

  const confirmDelete = (item: KnowledgeItem, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await api.delete('/semantic-layer/entities/' + itemToDelete.id);
      toast({ title: 'Deleted', description: `"${itemToDelete.name}" has been deleted.` });
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      if (detailItem?.id === itemToDelete.id) {
        setDetailOpen(false);
        setDetailItem(null);
      }
      fetchEntities();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Detail / Relationships
  // ---------------------------------------------------------------------------

  const openDetail = async (item: KnowledgeItem) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setRelationshipsLoading(true);
    try {
      const res = await api.get('/semantic-layer/entities/' + item.id);
      setDetailItem(res.data);
    } catch {
      setDetailItem(item);
    } finally {
      setDetailLoading(false);
    }

    try {
      const res = await api.get('/semantic-layer/entities/' + item.id + '/relationships');
      const data = res.data;
      setRelationships(Array.isArray(data) ? data : data.items ?? data.relationships ?? []);
    } catch {
      setRelationships([]);
    } finally {
      setRelationshipsLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Add Relationship
  // ---------------------------------------------------------------------------

  const openRelDialog = () => {
    setRelForm({ targetSearch: '', target_entity_id: '', relationship_type: '', description: '' });
    setRelSearchResults([]);
    setRelDialogOpen(true);
  };

  const searchRelTargets = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setRelSearchResults([]);
        return;
      }
      setRelSearchLoading(true);
      try {
        const res = await api.get('/semantic-layer/search', { params: { query: query.trim() } });
        const data = res.data;
        const results: KnowledgeItem[] = Array.isArray(data) ? data : data.items ?? data.entities ?? [];
        setRelSearchResults(results.filter((r) => r.id !== detailItem?.id));
      } catch {
        setRelSearchResults([]);
      } finally {
        setRelSearchLoading(false);
      }
    },
    [detailItem],
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      if (relDialogOpen && relForm.targetSearch.trim()) {
        searchRelTargets(relForm.targetSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [relForm.targetSearch, relDialogOpen, searchRelTargets]);

  const handleRelSave = async () => {
    if (!relForm.target_entity_id) {
      toast({ title: 'Validation', description: 'Select a target entity.', variant: 'destructive' });
      return;
    }
    if (!relForm.relationship_type.trim()) {
      toast({ title: 'Validation', description: 'Relationship type is required.', variant: 'destructive' });
      return;
    }
    if (!detailItem) return;

    setRelSaving(true);
    try {
      await api.post('/semantic-layer/relationships', {
        from_entity_id: detailItem.id,
        to_entity_id: relForm.target_entity_id,
        relationship_type: relForm.relationship_type.trim(),
        description: relForm.description.trim(),
      });
      toast({ title: 'Relationship created', description: 'Entities have been linked.' });
      setRelDialogOpen(false);
      const res = await api.get('/semantic-layer/entities/' + detailItem.id + '/relationships');
      const data = res.data;
      setRelationships(Array.isArray(data) ? data : data.items ?? data.relationships ?? []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create relationship';
      toast({ title: 'Error', description: message, variant: 'destructive' });
    } finally {
      setRelSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Pagination
  // ---------------------------------------------------------------------------

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const goToPage = (page: number) => {
    setOffset((page - 1) * PAGE_SIZE);
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderTypeBadge = (type: string) => (
    <Badge variant="outline" className={`inline-flex items-center gap-1.5 ${getTypeBadgeClass(type)}`}>
      {formatTypeLabel(type)}
    </Badge>
  );

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, i) => (
      <TableRow key={`skeleton-${i}`}>
        {Array.from({ length: 7 }).map((__, j) => (
          <TableCell key={j}>
            <div className="h-4 w-full animate-pulse rounded bg-muted" />
          </TableCell>
        ))}
      </TableRow>
    ));

  // ---------------------------------------------------------------------------
  // Tab content renderers
  // ---------------------------------------------------------------------------

  const renderDocumentsPlaceholder = () => (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <FileText className="h-10 w-10 mb-3 opacity-40" />
      <p className="text-sm font-medium">Content store indexing coming soon</p>
      <p className="text-xs mt-1">Documents from the content store will appear here once indexing is enabled.</p>
    </div>
  );

  const renderMemoriesPlaceholder = () => (
    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
      <BookOpen className="h-10 w-10 mb-3 opacity-40" />
      <p className="text-sm font-medium">Memory browsing coming soon</p>
      <p className="text-xs mt-1">Agent memories will be browsable here in a future update.</p>
    </div>
  );

  const renderTable = (items: KnowledgeItem[], showLoading: boolean) => (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[140px]">Type</TableHead>
              <TableHead>Name</TableHead>
              <TableHead className="hidden md:table-cell">Content / Description</TableHead>
              <TableHead className="hidden lg:table-cell">Category</TableHead>
              <TableHead className="hidden xl:table-cell">Source</TableHead>
              <TableHead className="hidden sm:table-cell w-[110px]">Updated</TableHead>
              <TableHead className="w-[100px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {showLoading ? (
              renderSkeletonRows()
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                    <Brain className="h-10 w-10 mb-3 opacity-40" />
                    <p className="text-sm font-medium">No knowledge items found</p>
                    <p className="text-xs mt-1">
                      {searchQuery || typeFilter !== 'all'
                        ? 'Try adjusting your search or filter.'
                        : 'Add your first knowledge item to get started.'}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow
                  key={item.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => openDetail(item)}
                >
                  <TableCell>{renderTypeBadge(item.entity_type)}</TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell className="hidden md:table-cell text-muted-foreground text-sm">
                    {truncate(item.description, 80)}
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {item.category ?? (item.properties as Record<string, unknown>)?.category as string ?? '-'}
                  </TableCell>
                  <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">
                    {item.source ?? 'manual'}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">
                    {formatDate(item.updated_at)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={(e) => openEdit(item, e)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={(e) => confirmDelete(item, e)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Brain className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Knowledge Base</h1>
            <p className="text-sm text-muted-foreground">
              Unified context for your agents — entities, memories, and business context
            </p>
          </div>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          Add Knowledge
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="entities">Entities</TabsTrigger>
          <TabsTrigger value="memories">Memories</TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
        </TabsList>

        {/* Search & Filter Bar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center mt-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search knowledge..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {KNOWLEDGE_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* All tab */}
        <TabsContent value="all" className="mt-4">
          {renderTable(displayedItems, isLoading)}
        </TabsContent>

        {/* Entities tab */}
        <TabsContent value="entities" className="mt-4">
          {renderTable(entities, loading)}
        </TabsContent>

        {/* Memories tab */}
        <TabsContent value="memories" className="mt-4">
          {!memoriesAvailable ? (
            renderMemoriesPlaceholder()
          ) : (
            renderTable(memories, memoriesLoading)
          )}
        </TabsContent>

        {/* Documents tab */}
        <TabsContent value="documents" className="mt-4">
          {renderDocumentsPlaceholder()}
        </TabsContent>
      </Tabs>

      {/* Pagination (for entities-based tabs) */}
      {!loading && totalCount > PAGE_SIZE && activeTab !== 'documents' && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, totalCount)} of {totalCount}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => goToPage(currentPage - 1)}
            >
              Previous
            </Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let page: number;
              if (totalPages <= 5) {
                page = i + 1;
              } else if (currentPage <= 3) {
                page = i + 1;
              } else if (currentPage >= totalPages - 2) {
                page = totalPages - 4 + i;
              } else {
                page = currentPage - 2 + i;
              }
              return (
                <Button
                  key={page}
                  variant={page === currentPage ? 'default' : 'outline'}
                  size="sm"
                  className="w-9"
                  onClick={() => goToPage(page)}
                >
                  {page}
                </Button>
              );
            })}
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => goToPage(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* ================================================================= */}
      {/* Create / Edit Knowledge Dialog                                    */}
      {/* ================================================================= */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Knowledge' : 'Add Knowledge'}</DialogTitle>
            <DialogDescription>
              {editingItem
                ? 'Update the knowledge item details below.'
                : 'Define a new knowledge item for your agents.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            {/* Type */}
            <div className="grid gap-2">
              <Label htmlFor="knowledge_type">Type</Label>
              <Select
                value={formData.entity_type}
                onValueChange={(v) => {
                  setFormData((prev) => ({
                    ...prev,
                    entity_type: v,
                    properties:
                      prev.properties.trim() === '' ||
                      prev.properties === PROPERTIES_PLACEHOLDER[prev.entity_type]
                        ? PROPERTIES_PLACEHOLDER[v] ?? '{}'
                        : prev.properties,
                  }));
                }}
              >
                <SelectTrigger id="knowledge_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KNOWLEDGE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Name */}
            <div className="grid gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Monthly Revenue, Customer Onboarding"
              />
            </div>

            {/* Content / Description */}
            <div className="grid gap-2">
              <Label htmlFor="description">Content / Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Describe what this knowledge item represents..."
                rows={3}
              />
            </div>

            {/* Category */}
            <div className="grid gap-2">
              <Label htmlFor="category">Category (optional)</Label>
              <Input
                id="category"
                value={formData.category}
                onChange={(e) => setFormData((prev) => ({ ...prev, category: e.target.value }))}
                placeholder="e.g. Finance, Operations, Engineering"
              />
            </div>

            {/* Tags */}
            <div className="grid gap-2">
              <Label htmlFor="tags">Tags (comma-separated)</Label>
              <Input
                id="tags"
                value={formData.tags}
                onChange={(e) => setFormData((prev) => ({ ...prev, tags: e.target.value }))}
                placeholder="finance, revenue, monthly"
              />
            </div>

            {/* Properties */}
            <div className="grid gap-2">
              <Label htmlFor="properties">Properties (optional JSON)</Label>
              <Textarea
                id="properties"
                value={formData.properties}
                onChange={(e) => setFormData((prev) => ({ ...prev, properties: e.target.value }))}
                className="font-mono text-sm"
                rows={5}
                placeholder="{}"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={formSaving}>
              Cancel
            </Button>
            <Button onClick={handleFormSave} disabled={formSaving}>
              {formSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingItem ? 'Save Changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================= */}
      {/* Knowledge Detail Dialog                                           */}
      {/* ================================================================= */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {detailItem && renderTypeBadge(detailItem.entity_type)}
              {detailItem?.name ?? 'Knowledge Detail'}
            </DialogTitle>
            <DialogDescription>
              Full details and relationships
            </DialogDescription>
          </DialogHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : detailItem ? (
            <ScrollArea className="flex-1 pr-2">
              <Tabs defaultValue="details" className="w-full">
                <TabsList className="w-full">
                  <TabsTrigger value="details" className="flex-1">Details</TabsTrigger>
                  <TabsTrigger value="properties" className="flex-1">Properties</TabsTrigger>
                  <TabsTrigger value="relationships" className="flex-1">
                    Relationships
                    {relationships.length > 0 && (
                      <Badge variant="secondary" className="ml-1.5 text-xs px-1.5 py-0">
                        {relationships.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                {/* Details tab */}
                <TabsContent value="details" className="mt-4 space-y-4">
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase tracking-wider">Description</Label>
                    <p className="text-sm whitespace-pre-wrap">
                      {detailItem.description || 'No description provided.'}
                    </p>
                  </div>

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-xs uppercase tracking-wider">Category</Label>
                      <p className="text-sm">
                        {detailItem.category ?? (detailItem.properties as Record<string, unknown>)?.category as string ?? '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-xs uppercase tracking-wider">Source</Label>
                      <p className="text-sm">{detailItem.source ?? 'manual'}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-xs uppercase tracking-wider">Created</Label>
                      <p className="text-sm">{formatDate(detailItem.created_at)}</p>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-muted-foreground text-xs uppercase tracking-wider">Updated</Label>
                      <p className="text-sm">{formatDate(detailItem.updated_at)}</p>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase tracking-wider">Tags</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {(detailItem.tags ?? []).length === 0 ? (
                        <p className="text-sm text-muted-foreground">No tags</p>
                      ) : (
                        detailItem.tags.map((tag) => (
                          <Badge key={tag} variant="secondary" className="text-xs">
                            <Tag className="mr-1 h-3 w-3" />
                            {tag}
                          </Badge>
                        ))
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Properties tab */}
                <TabsContent value="properties" className="mt-4">
                  {Object.keys(detailItem.properties ?? {}).length === 0 ? (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      No properties defined.
                    </p>
                  ) : (
                    <div className="rounded-md bg-muted p-4 space-y-2">
                      {Object.entries(detailItem.properties).map(([key, value]) => (
                        <div key={key} className="flex items-start gap-2">
                          <span className="text-sm font-medium text-muted-foreground min-w-[120px]">{key}:</span>
                          <span className="text-sm">
                            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </TabsContent>

                {/* Relationships tab */}
                <TabsContent value="relationships" className="mt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-muted-foreground text-xs uppercase tracking-wider">
                      Linked Entities
                    </Label>
                    <Button variant="outline" size="sm" className="gap-1.5" onClick={openRelDialog}>
                      <Plus className="h-3.5 w-3.5" />
                      Add Relationship
                    </Button>
                  </div>

                  {relationshipsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : relationships.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                      <Link2 className="h-8 w-8 mb-2 opacity-40" />
                      <p className="text-sm">No relationships yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {relationships.map((rel) => {
                        const targetEntity =
                          rel.from_entity_id === detailItem.id ? rel.to_entity : rel.from_entity;
                        const targetName =
                          targetEntity?.name ??
                          (rel.from_entity_id === detailItem.id
                            ? rel.to_entity_id
                            : rel.from_entity_id);
                        const targetType = targetEntity?.entity_type;

                        return (
                          <Card key={rel.id} className="p-3">
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <Badge variant="outline" className="shrink-0 text-xs">
                                  {rel.relationship_type}
                                </Badge>
                                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                <div className="flex items-center gap-1.5 min-w-0">
                                  {targetType && renderTypeBadge(targetType)}
                                  <span className="text-sm font-medium truncate">
                                    {targetName}
                                  </span>
                                </div>
                              </div>
                            </div>
                            {rel.description && (
                              <p className="text-xs text-muted-foreground mt-1.5 ml-1">
                                {rel.description}
                              </p>
                            )}
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            </ScrollArea>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* ================================================================= */}
      {/* Add Relationship Dialog                                           */}
      {/* ================================================================= */}
      <Dialog open={relDialogOpen} onOpenChange={setRelDialogOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Add Relationship</DialogTitle>
            <DialogDescription>
              Link &quot;{detailItem?.name}&quot; to another entity.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            {/* Target entity search */}
            <div className="grid gap-2">
              <Label htmlFor="rel-target-search">Target Entity</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="rel-target-search"
                  value={relForm.targetSearch}
                  onChange={(e) =>
                    setRelForm((prev) => ({ ...prev, targetSearch: e.target.value, target_entity_id: '' }))
                  }
                  placeholder="Search for an entity..."
                  className="pl-9"
                />
              </div>
              {relForm.targetSearch.trim() && (
                <div className="rounded-md border max-h-[180px] overflow-y-auto">
                  {relSearchLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  ) : relSearchResults.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No results</p>
                  ) : (
                    relSearchResults.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 flex items-center gap-2 ${
                          relForm.target_entity_id === r.id ? 'bg-muted' : ''
                        }`}
                        onClick={() =>
                          setRelForm((prev) => ({
                            ...prev,
                            target_entity_id: r.id,
                            targetSearch: r.name,
                          }))
                        }
                      >
                        {renderTypeBadge(r.entity_type)}
                        <span className="truncate">{r.name}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
              {relForm.target_entity_id && (
                <p className="text-xs text-green-600 dark:text-green-400">
                  Selected: {relForm.targetSearch}
                </p>
              )}
            </div>

            {/* Relationship type */}
            <div className="grid gap-2">
              <Label htmlFor="rel-type">Relationship Type</Label>
              <Input
                id="rel-type"
                value={relForm.relationship_type}
                onChange={(e) => setRelForm((prev) => ({ ...prev, relationship_type: e.target.value }))}
                placeholder="e.g. has_many, belongs_to, uses"
              />
            </div>

            {/* Description */}
            <div className="grid gap-2">
              <Label htmlFor="rel-desc">Description</Label>
              <Textarea
                id="rel-desc"
                value={relForm.description}
                onChange={(e) => setRelForm((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the relationship..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setRelDialogOpen(false)} disabled={relSaving}>
              Cancel
            </Button>
            <Button onClick={handleRelSave} disabled={relSaving || !relForm.target_entity_id}>
              {relSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Relationship
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ================================================================= */}
      {/* Delete Confirmation Dialog                                        */}
      {/* ================================================================= */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Delete Knowledge Item</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{itemToDelete?.name}&quot;? This action cannot be undone.
              Any relationships to this item will also be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default KnowledgePage;
